import type { Locale } from 'date-fns';
import { format, formatDistanceToNow } from 'date-fns';
import { Battery, BatteryLow, BatteryMedium, BatteryFull, AlertTriangle, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Device, Driver, Geofence, Position } from '@/lib/traccar-api';
import { isPointInGeofence } from '@/lib/geofence-utils';

type TFn = (key: string, fallback?: string) => string;
const defaultT: TFn = (_key, fallback) => fallback ?? _key;

interface DeviceRowContentProps {
  device: Device;
  position?: Position;
  locale?: Locale;
  t?: TFn;
  indent?: boolean;
  wrap?: boolean;
  drivers?: Driver[];
  geofences?: Geofence[];
}

export function DeviceRowContent({ device, position, locale, t = defaultT, indent = true, wrap = true, drivers, geofences }: DeviceRowContentProps) {
  const attrs = position?.attributes ?? {};
  const ignition = attrs.ignition as boolean | undefined;
  const blocked = attrs.blocked as boolean | undefined;
  const alarm = attrs.alarm as string | undefined;
  const batteryLevel = attrs.batteryLevel as number | undefined;
  const sat = attrs.sat as number | undefined;
  const gpsValid = position?.valid;
  const odometerM = attrs.odometer != null ? Number(attrs.odometer) : attrs.totalDistance != null ? Number(attrs.totalDistance) : undefined;
  const odometerKm = odometerM != null ? Math.round(odometerM / 1000).toLocaleString() : undefined;
  const power = attrs.power != null ? Number(attrs.power) : undefined;
  const rpm = attrs.rpm != null ? Number(attrs.rpm) : undefined;

  const lastUpdateTime = position?.fixTime ?? device.lastUpdate ?? null;
  const lastUpdateDate = lastUpdateTime ? new Date(lastUpdateTime) : null;
  const lastUpdateText = lastUpdateDate
    ? (() => {
        const ageMs = Date.now() - lastUpdateDate.getTime();
        return ageMs < 30 * 60 * 1000
          ? format(lastUpdateDate, 'HH:mm:ss')
          : formatDistanceToNow(lastUpdateDate, { addSuffix: true, locale });
      })()
    : '';
  const lastUpdateTitle = lastUpdateDate ? format(lastUpdateDate, 'dd/MM/yyyy HH:mm:ss') : '';

  const driverUniqueId = (attrs.driverUniqueId as string | undefined)?.trim();
  const driverName = driverUniqueId
    ? (drivers?.find(d => d.uniqueId === driverUniqueId)?.name ?? driverUniqueId)
    : null;

  const geofenceName = position && geofences?.find(g => isPointInGeofence(position.latitude, position.longitude, g.area))?.name;
  const addressText = geofenceName || position?.address;
  const addressTitle = geofenceName && position?.address ? `${geofenceName} — ${position.address}` : addressText;

  const hasIndicators = ignition !== undefined || !!position || blocked !== undefined || (batteryLevel !== undefined && power == null) || !!alarm || !!position?.protocol || !!device.model || odometerKm != null || power != null;

  const ignLabel = ignition ? t('performance.on', 'ON') : t('performance.off', 'OFF');
  const noFixLabel = t('map.noFix', 'No fix');
  const blockedLabel = blocked ? t('map.blocked', 'Engine blocked') : t('map.unblocked', 'Engine unblocked');
  const batteryLabel = t('performance.battery', 'Battery');
  const satellitesLabel = t('performance.satellites', 'satellites');
  const alarmLabel = t('notifications.attributes.alarm', 'Alarm');
  const alarmValue = alarm ? t(`notifications.alarms.${alarm}`, alarm) : '';

  return (
    <>
      {hasIndicators && (
        <div {...(!wrap ? { 'data-indicators': '' } : {})} className={cn('flex items-center gap-2 mt-1.5 min-w-0', wrap ? 'flex-wrap' : 'flex-nowrap overflow-hidden', indent && 'ml-[18px]')}>
          {ignition !== undefined && (
            <span title={`${t('performance.ignition', 'Ignition')} ${ignLabel}`} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', ignition ? 'text-green-600' : 'text-muted-foreground')}>
              <i className={ignition ? 'i-ignition-on' : 'i-ignition-off'} style={{ fontSize: '15px' }} />
              <span>{ignLabel}</span>
            </span>
          )}

          {position && (
            <span title={gpsValid === false ? noFixLabel : sat !== undefined ? `${sat} ${satellitesLabel}` : 'GPS'} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', gpsValid === false ? 'text-red-500' : sat !== undefined && sat >= 4 ? 'text-green-600' : 'text-muted-foreground')}>
              <i className={gpsValid === false ? 'i-sat-off' : 'i-sat'} style={{ fontSize: '15px' }} />
              {gpsValid !== false && sat !== undefined && <span>{sat}</span>}
            </span>
          )}

          {blocked !== undefined && (
            <span title={blockedLabel} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', blocked ? 'text-red-500' : 'text-green-600')}>
              <i className={blocked ? 'i-lock-2' : 'i-lock'} style={{ fontSize: '15px' }} />
            </span>
          )}

          {batteryLevel !== undefined && power == null && (() => {
            const BatteryIcon = batteryLevel > 75 ? BatteryFull : batteryLevel > 40 ? BatteryMedium : batteryLevel > 15 ? BatteryLow : Battery;
            const color = batteryLevel > 50 ? 'text-green-600' : batteryLevel > 20 ? 'text-yellow-600' : 'text-red-600';
            return (
              <span title={`${batteryLabel} ${Math.round(batteryLevel)}%`} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', color)}>
                <BatteryIcon className="h-4 w-4" />
              </span>
            );
          })()}

          {alarm && (
            <span title={`${alarmLabel}: ${alarmValue}`} className="flex flex-shrink-0 items-center gap-0.5 text-[11px] text-red-500">
              <AlertTriangle className="h-3 w-3" />
              <span>{alarmValue}</span>
            </span>
          )}

          {power != null && (
            <span title={`Power ${power.toFixed(1)} V`} className="flex flex-shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
              <i className="i-battery-con" style={{ fontSize: '15px' }} />
              <span>{power.toFixed(1)} V</span>
            </span>
          )}

          {rpm != null && (
            <span title={`RPM ${Math.round(rpm)}`} className="flex flex-shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
              <i className="i-revolutions" style={{ fontSize: '15px' }} />
              <span>{Math.round(rpm)} rpm</span>
            </span>
          )}

          {odometerKm != null && (
            <span title={`Odometer ${odometerKm} km`} className="flex-shrink-0 text-[11px] text-muted-foreground">
              {odometerKm} km
            </span>
          )}

          {driverName && (
            <span title={driverName} className="flex flex-shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
              <User className="h-3 w-3 flex-shrink-0" />
              <span className="truncate max-w-[100px]">{driverName}</span>
            </span>
          )}

          {(position?.protocol || device.model) && (
            <span title={`Protocol: ${position?.protocol || device.model}`} className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground font-mono leading-none">
              {position?.protocol || device.model}
            </span>
          )}
        </div>
      )}

      {(addressText || lastUpdateText) && (
        <div className={cn('flex items-center gap-2 mt-1 text-[12px] text-muted-foreground min-w-0', indent && 'ml-[18px]')}>
          {addressText && <span title={addressTitle} className="truncate flex-1 min-w-0">{addressText}</span>}
          {lastUpdateText && <span title={lastUpdateTitle} className="flex-shrink-0 text-[12px]">{lastUpdateText}</span>}
        </div>
      )}
    </>
  );
}
